import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Fade,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import React, { useEffect, useMemo, useState } from "react";
import { useNotify } from "../../context/NotificationContext";
import { APIS } from "../../modules/common/api/apis";
import { get, patch } from "../../modules/common/shared/AxiosAPI";
import { EditableSystemSettings, LOGGING_LEVELS, LoggingLevel, SystemSettings } from "../../modules/common/shared/types";
import { getApiErrorMessage } from "../../modules/common/shared/util/ApiErrorUtil";

const LOGGING_LEVEL_INFO: Record<LoggingLevel, { label: string; description: string }> = {
  ERROR: { label: "Error", description: "Only failures that need attention." },
  WARN: { label: "Warning", description: "Failures plus anything unusual worth a look." },
  INFO: { label: "Info", description: "Normal activity. Recommended for everyday use." },
  DEBUG: { label: "Debug", description: "Everything, for troubleshooting. Very large logs." },
};

const EDITABLE_KEYS: (keyof EditableSystemSettings)[] = ["loggingLevel", "attendanceMessageSendingEnabled"];

const toEditable = (s: SystemSettings): EditableSystemSettings => ({
  loggingLevel: s.loggingLevel,
  attendanceMessageSendingEnabled: s.attendanceMessageSendingEnabled,
});

// Only the keys whose draft differs from what the server has; this is the PATCH body.
const changedKeys = (saved: EditableSystemSettings, draft: EditableSystemSettings): Partial<EditableSystemSettings> =>
  EDITABLE_KEYS.reduce<Partial<EditableSystemSettings>>((acc, key) => {
    if (saved[key] !== draft[key]) (acc as Record<string, unknown>)[key] = draft[key];
    return acc;
  }, {});

const formatResetTime = (iso?: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

// ---------------- Building blocks ----------------

const SettingsSection = ({ icon, title, description, children }: { icon: React.ReactNode; title: string; description: string; children: React.ReactNode }) => (
  <Card elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 3, boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)" }}>
    <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", p: { xs: 2, sm: 2.5 }, borderBottom: "1px solid #eef2f6" }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "rgba(25, 118, 210, 0.08)",
          color: "primary.main",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>{title}</Typography>
        <Typography sx={{ fontSize: 13.5, color: "text.secondary", mt: 0.25 }}>{description}</Typography>
      </Box>
    </Box>
    <Box sx={{ px: { xs: 2, sm: 2.5 } }}>{children}</Box>
  </Card>
);

const SettingRow = ({ label, description, changed, children }: { label: string; description: React.ReactNode; changed: boolean; children: React.ReactNode }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      alignItems: { xs: "stretch", sm: "center" },
      gap: { xs: 1.5, sm: 3 },
      py: 2.25,
      "& + &": { borderTop: "1px solid #eef2f6" },
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={{ fontWeight: 600, fontSize: 15, color: "#0f172a" }}>{label}</Typography>
        {changed && <Chip label="Unsaved" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: 11, fontWeight: 600 }} />}
      </Stack>
      <Box sx={{ fontSize: 13.5, color: "text.secondary", mt: 0.5 }}>{description}</Box>
    </Box>
    <Box sx={{ flexShrink: 0 }}>{children}</Box>
  </Box>
);

const LoadingState = () => (
  <Stack spacing={2.5}>
    {[0, 1].map((i) => (
      <Card key={i} elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 3, p: 2.5 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Skeleton variant="rounded" width={40} height={40} />
          <Box sx={{ flex: 1 }}>
            <Skeleton width="30%" />
            <Skeleton width="55%" />
          </Box>
        </Stack>
        <Skeleton variant="rounded" height={44} sx={{ mt: 2.5 }} />
      </Card>
    ))}
  </Stack>
);

// ---------------- Page ----------------

// embedded: shown as the System section of Settings, which supplies the page title
export default function SystemSettingsPage({ embedded = false }: { embedded?: boolean }) {
  const notify = useNotify();
  const [saved, setSaved] = useState<SystemSettings | null>(null);
  const [draft, setDraft] = useState<EditableSystemSettings | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const load = () => {
    setLoadError(null);
    setSaved(null);
    get(APIS.SYSTEM_SETTINGS, null)
      .then((res) => {
        setSaved(res.data);
        setDraft(toEditable(res.data));
      })
      .catch((err) => setLoadError(getApiErrorMessage(err, "Couldn't load settings.")));
  };

  useEffect(load, []);

  const changes = useMemo(() => (saved && draft ? changedKeys(toEditable(saved), draft) : {}), [saved, draft]);
  const hasChanges = Object.keys(changes).length > 0;

  const update = <K extends keyof EditableSystemSettings>(key: K, value: EditableSystemSettings[K]) => {
    setSaveError(null);
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  };

  const discard = () => {
    if (saved) setDraft(toEditable(saved));
    setSaveError(null);
  };

  const save = async () => {
    if (!hasChanges || isSaving) return;
    // Same rule the backend enforces: only known log levels are accepted.
    if (changes.loggingLevel && !LOGGING_LEVELS.includes(changes.loggingLevel)) {
      setSaveError("Choose a valid logging level.");
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      const res = await patch(APIS.SYSTEM_SETTINGS, null, changes);
      setSaved(res.data);
      setDraft(toEditable(res.data));
      notify("Settings saved", "success");
    } catch (err) {
      setSaveError(getApiErrorMessage(err, "Couldn't save settings."));
    } finally {
      setIsSaving(false);
    }
  };

  const resetTime = formatResetTime(saved?.loggingLevelResetAt);

  return (
    <Box sx={embedded ? {} : { width: "100%", maxWidth: 880, mx: "auto", p: { xs: 2, sm: 3 }, pb: hasChanges ? 14 : 4 }}>
      <Box sx={{ mb: 3, display: embedded ? "none" : "block" }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 800, fontSize: { xs: "1.35rem", sm: "1.6rem" }, color: "#0f172a", letterSpacing: "-0.01em" }}>
          System Settings
        </Typography>
        <Typography sx={{ color: "text.secondary", fontSize: 14.5, mt: 0.5 }}>
          Settings for your school. Changes take effect as soon as you save.
        </Typography>
      </Box>

      {loadError ? (
        <Alert
          severity="error"
          sx={{ borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={load} sx={{ fontWeight: 600 }}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      ) : !saved || !draft ? (
        <LoadingState />
      ) : (
        <Stack spacing={2.5}>
          <SettingsSection
            icon={<NotificationsActiveRoundedIcon />}
            title="Notifications"
            description="Messages sent to parents and guardians."
          >
            <SettingRow
              label="Attendance message sending"
              description="Send an SMS/notification to guardians when attendance is marked."
              changed={"attendanceMessageSendingEnabled" in changes}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: draft.attendanceMessageSendingEnabled ? "success.main" : "text.secondary", minWidth: 24 }}>
                  {draft.attendanceMessageSendingEnabled ? "On" : "Off"}
                </Typography>
                <Switch
                  checked={draft.attendanceMessageSendingEnabled}
                  onChange={(e) => update("attendanceMessageSendingEnabled", e.target.checked)}
                  disabled={isSaving}
                  slotProps={{ input: { "aria-label": "Attendance message sending" } }}
                />
              </Stack>
            </SettingRow>
          </SettingsSection>

          <SettingsSection
            icon={<ReceiptLongRoundedIcon />}
            title="Logging"
            description="How much detail the server records for your school."
          >
            <SettingRow
              label="Logging level"
              changed={"loggingLevel" in changes}
              description={
                <>
                  {LOGGING_LEVEL_INFO[draft.loggingLevel].description}
                  {draft.loggingLevel === "DEBUG" && (
                    <Box component="span" sx={{ display: "block", mt: 0.5, color: "warning.dark", fontWeight: 500 }}>
                      {saved.loggingLevel === "DEBUG" && resetTime
                        ? `Automatically returns to Info at ${resetTime}.`
                        : "Automatically returns to Info after 1 hour."}
                    </Box>
                  )}
                </>
              }
            >
              <TextField
                select
                size="small"
                value={draft.loggingLevel}
                onChange={(e) => update("loggingLevel", e.target.value as LoggingLevel)}
                disabled={isSaving}
                sx={{ width: { xs: "100%", sm: 200 } }}
                slotProps={{ htmlInput: { "aria-label": "Logging level" } }}
              >
                {LOGGING_LEVELS.map((level) => (
                  <MenuItem key={level} value={level}>
                    {LOGGING_LEVEL_INFO[level].label}
                  </MenuItem>
                ))}
              </TextField>
            </SettingRow>
          </SettingsSection>
        </Stack>
      )}

      {/* Save bar: appears only when something has changed */}
      <Fade in={hasChanges} unmountOnExit>
        <Paper
          elevation={0}
          sx={{
            // In Settings the bar sticks to the bottom aligned with the form; standalone it floats centred
            ...(embedded
              ? { position: "sticky", bottom: { xs: 12, sm: 16 }, mt: 2.5 }
              : { position: "fixed", left: "50%", transform: "translateX(-50%)", bottom: { xs: 16, sm: 24 }, width: "calc(100% - 32px)", maxWidth: 640 }),
            zIndex: (theme) => (embedded ? theme.zIndex.appBar - 1 : theme.zIndex.snackbar - 1),
            borderRadius: 3,
            border: "1px solid #e5e7eb",
            boxShadow: "0 16px 40px rgba(15, 23, 42, 0.16)",
            p: { xs: 1.5, sm: 2 },
          }}
        >
          {saveError && (
            <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }}>
              {saveError}
            </Alert>
          )}
          <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "center" }} spacing={1.5}>
            <Typography sx={{ flex: 1, fontWeight: 600, fontSize: 14.5, color: "#0f172a" }}>
              You have unsaved changes
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button onClick={discard} disabled={isSaving} sx={{ flex: { xs: 1, sm: "none" }, textTransform: "none", fontWeight: 600, borderRadius: 2 }}>
                Discard
              </Button>
              <Button
                variant="contained"
                onClick={save}
                loading={isSaving}
                sx={{ flex: { xs: 1, sm: "none" }, textTransform: "none", fontWeight: 600, borderRadius: 2, px: 3, boxShadow: "none" }}
              >
                Save changes
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Fade>
    </Box>
  );
}
